import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { RadioButtonModule } from "primeng/radiobutton";
import { SignatureSkipType } from "../../../../../../../core/database/db";
import { BottomSheetComponent } from "../../../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { InputErrorComponent } from "../../../../../../../shared/components/input-error/input-error.component";
import { TextareaComponent } from "../../../../../../../shared/components/textarea/textarea.component";
import { FormFieldComponent } from "../../../../../../../shared/components/form-field/form-field.component";
import { CONSTANTS } from "../../../../../../../constants";

export interface SkipSignatureData {
  skipType: SignatureSkipType;
  h1Email: string;
  h2Email?: string;
  /** Applicable when skipType is RefusedToSign. */
  refusalReason?: string;
}

@Component({
  selector: "app-skip-signature-bottom-sheet",
  standalone: true,
  imports: [
    BottomSheetComponent,
    ReactiveFormsModule,
    ButtonModule,
    RadioButtonModule,
    InputErrorComponent,
    TextareaComponent,
    FormFieldComponent
  ],
  templateUrl: "./skip-signature-bottom-sheet.component.html",
  styleUrl: "./skip-signature-bottom-sheet.component.scss",
})
export class SkipSignatureBottomSheetComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() isSkipMode: boolean = false;
  @Input() initialH1Email: string = "";
  @Input() initialH2Email: string = "";

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() skipConfirmed = new EventEmitter<SkipSignatureData>();
  @Output() skipCleared = new EventEmitter<void>();

  readonly SignatureSkipType = SignatureSkipType;

  maxSignatureSkipReasonLength = CONSTANTS.CONSTRAINTS.MAX_SIGNATURE_SKIP_REASON;
  formSubmitted = false;

  private fb = new FormBuilder();

  form = this.fb.group({
    skipType:      [null as SignatureSkipType | null, Validators.required],
    h1Email:       ["", [Validators.required, Validators.email]],
    h2Email:       ["", Validators.email],
    refusalReason: ["", Validators.required],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isOpen"]?.currentValue === true) {
      this.formSubmitted = false;
      if (!this.isSkipMode) {
        this.form.reset({
          skipType:      null,
          h1Email:       this.initialH1Email ?? "",
          h2Email:       this.initialH2Email ?? "",
          refusalReason: "",
        });
      }
    }
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted));
  }

  onCancel(): void {
    this.formSubmitted = false;
    this.isOpenChange.emit(false);
  }

  onSave(): void {
    this.formSubmitted = true;

    // Conditionally require refusalReason when RefusedToSign is selected
    const refusalCtrl = this.form.get("refusalReason");
    if (this.form.get("skipType")?.value === SignatureSkipType.RefusedToSign
        && !refusalCtrl?.value?.trim()) {
      refusalCtrl?.setErrors({ required: true });
    } else {
      refusalCtrl?.setErrors(null);
    }

    if (this.form.invalid) {
      return;
    }
    const { skipType, h1Email, h2Email, refusalReason } = this.form.getRawValue();
    const resolvedSkipType = skipType ?? SignatureSkipType.RefusedToSign;
    this.skipConfirmed.emit({
      skipType: resolvedSkipType,
      h1Email: h1Email!,
      h2Email: h2Email?.trim() || undefined,
      refusalReason: resolvedSkipType === SignatureSkipType.RefusedToSign
        ? (refusalReason?.trim() || undefined)
        : undefined,
    });
    this.formSubmitted = false;
    this.isOpenChange.emit(false);
  }

  onUndoSkip(): void {
    this.skipCleared.emit();
    this.isOpenChange.emit(false);
  }
}
